import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Pending,
} from '@mui/icons-material';
import { systemSettingService } from '../../services/systemSettingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminApprovals: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState({
    invoices: [] as any[],
    employees: [] as any[],
    requests: [] as any[],
    payments: [] as any[],
    purchaseOrders: [] as any[],
  });

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const [invoices, employees, requests, payments, purchaseOrders] = await Promise.all([
        systemSettingService.getPendingApprovals('invoice').catch(() => ({ data: [] })),
        systemSettingService.getPendingApprovals('employee').catch(() => ({ data: [] })),
        systemSettingService.getPendingApprovals('request').catch(() => ({ data: [] })),
        systemSettingService.getPendingApprovals('payment').catch(() => ({ data: [] })),
        systemSettingService.getPendingApprovals('purchaseorder').catch(() => ({ data: [] })),
      ]);

      setPendingApprovals({
        invoices: invoices.data || [],
        employees: employees.data || [],
        requests: requests.data || [],
        payments: payments.data || [],
        purchaseOrders: purchaseOrders.data || [],
      });
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (type: string, id: number) => {
    try {
      await systemSettingService.approveItem(type, id);
      alert('Item approved successfully');
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error approving item:', error);
      alert('Failed to approve item');
    }
  };

  const handleReject = async (type: string, id: number) => {
    if (!window.confirm('Are you sure you want to reject and delete this item?')) {
      return;
    }
    try {
      await systemSettingService.rejectItem(type, id);
      alert('Item rejected successfully');
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error rejecting item:', error);
      alert('Failed to reject item');
    }
  };

  const ApprovalItem = ({ item, type }: { item: any; type: string }) => (
    <>
      <ListItem sx={{ py: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Pending sx={{ color: 'warning.main' }} />
            <Typography variant="body1" fontWeight="medium">
              {item.title || item.name || `${type} #${item.id}`}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(item.createdAt || Date.now()).toLocaleDateString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="success" size="small" startIcon={<CheckCircle />} onClick={() => handleApprove(type, item.id)}>
            Approve
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<Cancel />} onClick={() => handleReject(type, item.id)}>
            Reject
          </Button>
        </Box>
      </ListItem>
      <Divider />
    </>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Admin Approvals</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>Admin Approvals</Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Items below require your approval before they become active in the system.
        </Alert>

        <Card elevation={2}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label={<Box sx={{ display: 'flex', gap: 1 }}>{t('approvals.invoices')} {pendingApprovals.invoices.length > 0 && <Chip label={pendingApprovals.invoices.length} size="small" color="warning" />}</Box>} />
              <Tab label={<Box sx={{ display: 'flex', gap: 1 }}>{t('approvals.employees')} {pendingApprovals.employees.length > 0 && <Chip label={pendingApprovals.employees.length} size="small" color="warning" />}</Box>} />
              <Tab label={<Box sx={{ display: 'flex', gap: 1 }}>{t('approvals.requests')} {pendingApprovals.requests.length > 0 && <Chip label={pendingApprovals.requests.length} size="small" color="warning" />}</Box>} />
              <Tab label={<Box sx={{ display: 'flex', gap: 1 }}>{t('approvals.payments')} {pendingApprovals.payments.length > 0 && <Chip label={pendingApprovals.payments.length} size="small" color="warning" />}</Box>} />
              <Tab label={<Box sx={{ display: 'flex', gap: 1 }}>{t('approvals.purchaseOrders')} {pendingApprovals.purchaseOrders.length > 0 && <Chip label={pendingApprovals.purchaseOrders.length} size="small" color="warning" />}</Box>} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <List>
              {pendingApprovals.invoices.length > 0 ? (
                pendingApprovals.invoices.map((invoice) => (
                  <ApprovalItem key={invoice.id} item={invoice} type="invoice" />
                ))
              ) : (
                <ListItem><ListItemText primary={t('approvals.noPending')} secondary={t('approvals.allApproved')} /></ListItem>
              )}
            </List>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <List>
              {pendingApprovals.employees.length > 0 ? (
                pendingApprovals.employees.map((employee) => (
                  <ApprovalItem key={employee.id} item={employee} type="employee" />
                ))
              ) : (
                <ListItem><ListItemText primary="No pending employee approvals" /></ListItem>
              )}
            </List>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <List>
              {pendingApprovals.requests.length > 0 ? (
                pendingApprovals.requests.map((request) => (
                  <ApprovalItem key={request.id} item={request} type="request" />
                ))
              ) : (
                <ListItem><ListItemText primary="No pending request approvals" /></ListItem>
              )}
            </List>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <List>
              {pendingApprovals.payments.length > 0 ? (
                pendingApprovals.payments.map((payment) => (
                  <ApprovalItem key={payment.id} item={payment} type="payment" />
                ))
              ) : (
                <ListItem><ListItemText primary="No pending payment approvals" /></ListItem>
              )}
            </List>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <List>
              {pendingApprovals.purchaseOrders.length > 0 ? (
                pendingApprovals.purchaseOrders.map((order) => (
                  <ApprovalItem key={order.id} item={order} type="purchaseorder" />
                ))
              ) : (
                <ListItem><ListItemText primary="No pending purchase order approvals" /></ListItem>
              )}
            </List>
          </TabPanel>
        </Card>
      </Box>
  );
};

export default AdminApprovals;
